import { useLayoutEffect, useMemo } from "react";
import { useFlagOr, useText } from "../settings/context";
import { fileUrl } from "../settings/fichier";
import {
  DEFAULT_BRAND,
  DEFAULT_PAGE,
  palette,
  veilOf,
  type Gradient,
  type PageImage,
} from "./palette";

export { DEFAULT_BRAND, DEFAULT_PAGE };
/** Le style d'un dégradé allumé sans style choisi : celui du réglage, par défaut. */
export const DEFAULT_GRADIENT_STYLE = "halos";
/** Le voile d'une image de fond sans voile choisi. */
export const DEFAULT_VEIL = "medium";

/**
 * Paints the palette ONTO THE SITE, and nowhere else.
 *
 * `.site`, not `:root`: the client's colours belong to the client's site. Put
 * them on the document and the organiser bar, the settings panel and the recap
 * would take the event's hue too — and an organiser working on a navy-blue event
 * would face a navy-blue editor on a navy-blue site, with nothing left to tell
 * the tool from the thing being configured.
 *
 * Applied through inline custom properties rather than a stylesheet: they are
 * CONCRETE VALUES, computed in JS, and they must beat the tokens file without a
 * specificity contest.
 *
 * ⚠️ `useLayoutEffect`, not `useEffect`: the paint must land before the browser
 * draws, or every colour change shows a frame of the old palette.
 *
 * ⚠️ IT TAKES THE ELEMENT, NOT A REF, and that is what fixes a real regression. A
 * `RefObject` never changes identity, so the effect only re-ran when the COLOURS
 * changed — and the loading gate unmounts the site until the event's configuration
 * is in hand. The site therefore mounted on a render where the palette was
 * unchanged, the effect did not fire, and it received NOTHING: measured, zero inline
 * properties, the whole event's palette silently replaced by the stylesheet
 * defaults. Passing the node makes its arrival a dependency, which is what it is.
 */
/**
 * LE DÉGRADÉ DU FOND, tel que réglé — ou `null` s'il est éteint. Lu comme les deux
 * couleurs, dans la cascade des langues : c'est le même réglage d'apparence qu'elles.
 * Qu'il s'applique ou non (deux couleurs de bords opposés), `palette` en décide.
 */
export function usePageGradient(): Gradient | null {
  const on = useFlagOr("FO_MEETING_V2_PAGE_GRADIENT");
  const style = useText("FO_MEETING_V2_PAGE_GRADIENT_STYLE", DEFAULT_GRADIENT_STYLE);
  const color = useText("FO_MEETING_V2_PAGE_COLOR_2", "");
  return useMemo(() => (on ? { style, color } : null), [on, style, color]);
}

/**
 * L'IMAGE DE FOND, et son voile — ou `null` sans image. Passée par `fileUrl` comme les
 * bandeaux : une valeur peut être une clé de fichier plutôt qu'une adresse.
 */
export function usePageImage(): PageImage | null {
  const url = fileUrl(useText("FO_MEETING_V2_PAGE_IMAGE", ""));
  const veil = useText("FO_MEETING_V2_PAGE_IMAGE_VEIL", DEFAULT_VEIL);
  return useMemo(() => (url ? { url, veil: veilOf(veil) } : null), [url, veil]);
}

export function useTheme(target: HTMLElement | null): {
  brand: string;
  page: string;
} {
  const brand = useText("FO_MEETING_V2_BRAND_COLOR", DEFAULT_BRAND);
  const page = useText("FO_MEETING_V2_PAGE_COLOR", DEFAULT_PAGE);
  const gradient = usePageGradient();
  const image = usePageImage();

  const tokens = useMemo(() => {
    try {
      return palette(brand, page, gradient, image);
    } catch {
      // A malformed value — a half-typed hex, a stray import — must not take the
      // site down. The default palette is a worse answer than the organiser's,
      // and a far better one than a blank page.
      return palette(DEFAULT_BRAND, DEFAULT_PAGE);
    }
  }, [brand, page, gradient, image]);

  useLayoutEffect(() => {
    if (!target) return;
    for (const [name, value] of Object.entries(tokens)) {
      target.style.setProperty(name, value);
    }
  }, [target, tokens]);

  return { brand, page };
}
